import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, Directive, Self, SkipSelf, Input, HostBinding, NgModule, isDevMode } from '@angular/core';
import { Subject, pipe, Subscription, BehaviorSubject, combineLatest, NEVER, timer, of, merge } from 'rxjs';
import { filter, map, shareReplay, tap, distinctUntilChanged, switchMap, first, auditTime } from 'rxjs/operators';
import * as i1 from '@angular/forms';
import { FormGroup, FormArray, FormControl } from '@angular/forms';

/**
 * Provides a way to add to available options for when to display an error for
 * an invalid control. Options that come by default are
 * `'touched'`, `'dirty'`, `'touchedAndDirty'`, `'formIsSubmitted'`.
 */
const CUSTOM_ERROR_STATE_MATCHERS = new InjectionToken('CUSTOM_ERROR_STATE_MATCHERS');
class ShowOnTouchedErrorStateMatcher {
  isErrorState(control, form) {
    return !!(control && control.invalid && (control.touched || form && form.submitted));
  }
  static {
    this.ɵfac = function ShowOnTouchedErrorStateMatcher_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ShowOnTouchedErrorStateMatcher)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: ShowOnTouchedErrorStateMatcher,
      factory: ShowOnTouchedErrorStateMatcher.ɵfac
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ShowOnTouchedErrorStateMatcher, [{
    type: Injectable
  }], null, null);
})();
class ShowOnDirtyErrorStateMatcher {
  isErrorState(control, form) {
    return !!(control && control.invalid && (control.dirty || form && form.submitted));
  }
  static {
    this.ɵfac = function ShowOnDirtyErrorStateMatcher_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ShowOnDirtyErrorStateMatcher)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: ShowOnDirtyErrorStateMatcher,
      factory: ShowOnDirtyErrorStateMatcher.ɵfac
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ShowOnDirtyErrorStateMatcher, [{
    type: Injectable
  }], null, null);
})();
class ShowOnTouchedAndDirtyErrorStateMatcher {
  isErrorState(control, form) {
    return !!(control && control.invalid && (control.dirty && control.touched || form && form.submitted));
  }
  static {
    this.ɵfac = function ShowOnTouchedAndDirtyErrorStateMatcher_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ShowOnTouchedAndDirtyErrorStateMatcher)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: ShowOnTouchedAndDirtyErrorStateMatcher,
      factory: ShowOnTouchedAndDirtyErrorStateMatcher.ɵfac
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ShowOnTouchedAndDirtyErrorStateMatcher, [{
    type: Injectable
  }], null, null);
})();
class ShowOnSubmittedErrorStateMatcher {
  isErrorState(control, form) {
    return !!(control && control.invalid && form && form.submitted);
  }
  static {
    this.ɵfac = function ShowOnSubmittedErrorStateMatcher_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ShowOnSubmittedErrorStateMatcher)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: ShowOnSubmittedErrorStateMatcher,
      factory: ShowOnSubmittedErrorStateMatcher.ɵfac
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ShowOnSubmittedErrorStateMatcher, [{
    type: Injectable
  }], null, null);
})();
class ErrorStateMatchers {
  constructor(showOnTouchedErrorStateMatcher, showOnDirtyErrorStateMatcher, showOnTouchedAndDirtyErrorStateMatcher, showOnSubmittedErrorStateMatcher, customErrorStateMatchers) {
    this.matchers = {};
    this.matchers['touched'] = showOnTouchedErrorStateMatcher;
    this.matchers['dirty'] = showOnDirtyErrorStateMatcher;
    this.matchers['touchedAndDirty'] = showOnTouchedAndDirtyErrorStateMatcher;
    this.matchers['formIsSubmitted'] = showOnSubmittedErrorStateMatcher;
    if (customErrorStateMatchers) {
      this.matchers = {
        ...this.matchers,
        ...customErrorStateMatchers
      };
    }
  }
  get(showWhen) {
    return this.matchers[showWhen];
  }
  validKeys() {
    return Object.keys(this.matchers);
  }
  static {
    this.ɵfac = function ErrorStateMatchers_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ErrorStateMatchers)(i0.ɵɵinject(ShowOnTouchedErrorStateMatcher), i0.ɵɵinject(ShowOnDirtyErrorStateMatcher), i0.ɵɵinject(ShowOnTouchedAndDirtyErrorStateMatcher), i0.ɵɵinject(ShowOnSubmittedErrorStateMatcher), i0.ɵɵinject(CUSTOM_ERROR_STATE_MATCHERS, 8));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: ErrorStateMatchers,
      factory: ErrorStateMatchers.ɵfac
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ErrorStateMatchers, [{
    type: Injectable
  }], function () {
    return [{
      type: ShowOnTouchedErrorStateMatcher
    }, {
      type: ShowOnDirtyErrorStateMatcher
    }, {
      type: ShowOnTouchedAndDirtyErrorStateMatcher
    }, {
      type: ShowOnSubmittedErrorStateMatcher
    }, {
      type: undefined,
      decorators: [{
        type: Optional
      }, {
        type: Inject,
        args: [CUSTOM_ERROR_STATE_MATCHERS]
      }]
    }];
  }, null);
})();
class ErrorsConfiguration {
  constructor() {
    this.showErrorsWhenInput = 'touched';
    this.showMaxErrors = undefined;
  }
  static {
    this.ɵfac = function ErrorsConfiguration_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ErrorsConfiguration)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: ErrorsConfiguration,
      factory: ErrorsConfiguration.ɵfac
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ErrorsConfiguration, [{
    type: Injectable
  }], null, null);
})();

/**
 * Extract a touched changed observable from an abstract control
 * @param control AbstractControl like object with markAsTouched method
 *
 * @usage
 * ```
 * const formControl = new FormControl();
 * const touchedChanged$ = extractTouchedChanges(formControl);
 * ```
 */
const extractTouchedChanges = control => {
  const prevMarkAsTouched = control.markAsTouched;
  const prevMarkAsUntouched = control.markAsUntouched;
  const touchedChanges$ = new Subject();
  function nextMarkAsTouched(...args) {
    touchedChanges$.next(true);
    prevMarkAsTouched.bind(control)(...args);
  }
  function nextMarkAsUntouched(...args) {
    touchedChanges$.next(false);
    prevMarkAsUntouched.bind(control)(...args);
  }
  control.markAsTouched = nextMarkAsTouched;
  control.markAsUntouched = nextMarkAsUntouched;
  return touchedChanges$.asObservable();
};
/**
 * Marks the provided control as well as all of its children as dirty
 * @param options to be passed into control.markAsDirty() call
 */
function markDescendantsAsDirty(control, options) {
  control.markAsDirty(options);
  if (control instanceof FormGroup || control instanceof FormArray) {
    const controls = Object.keys(control.controls).map(controlName => control.get(controlName));
    controls.forEach(c => {
      c.markAsDirty(options);
      if (c.controls) {
        markDescendantsAsDirty(c, options);
      }
    });
  }
}
function filterOutNullish() {
  return pipe(filter(x => x != null));
}
class NgxError extends Error {
  constructor(message) {
    super(`NgxError: ${message}`);
  }
}
class NoParentNgxErrorsError extends NgxError {
  constructor() {
    super('Directive ngxError requires a parent directive ngxErrors');
  }
}
class ValueMustBeStringError extends NgxError {
  constructor() {
    super('Directive ngxError requires a string value');
  }
}
class NoControlError extends NgxError {
  constructor() {
    super('Directive ngxErrors requires either control name or control instance');
  }
}
class ControlInstanceError extends NgxError {
  constructor() {
    super('Control must be either a FormGroup, FormControl or FormArray');
  }
}
class ControlNotFoundError extends NgxError {
  constructor(name) {
    super(`Control "${name}" could not be found`);
  }
}
class ParentFormGroupNotFoundError extends NgxError {
  constructor(name) {
    super(`Can't search for control "${name}" because parent FormGroup is not found`);
  }
}
class InvalidShowWhenError extends NgxError {
  constructor(showWhen, keys) {
    super(`Invalid showWhen value: ${showWhen}. Valid values are: ${keys.join(', ')}`);
  }
}
class NgxErrorsFormDirective {
  constructor(ngForm, formGroupDirective) {
    this.ngForm = ngForm;
    this.formGroupDirective = formGroupDirective;
  }
  get form() {
    return this.ngForm ?? this.formGroupDirective;
  }
  static {
    this.ɵfac = function NgxErrorsFormDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NgxErrorsFormDirective)(i0.ɵɵdirectiveInject(i1.NgForm, 10), i0.ɵɵdirectiveInject(i1.FormGroupDirective, 10));
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NgxErrorsFormDirective,
      selectors: [["form"]],
      exportAs: ["ngxErrorsForm"],
      standalone: false
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NgxErrorsFormDirective, [{
    type: Directive,
    args: [{
      // eslint-disable-next-line @angular-eslint/directive-selector
      selector: 'form',
      exportAs: 'ngxErrorsForm'
    }]
  }], function () {
    return [{
      type: i1.NgForm,
      decorators: [{
        type: Self
      }, {
        type: Optional
      }]
    }, {
      type: i1.FormGroupDirective,
      decorators: [{
        type: Self
      }, {
        type: Optional
      }]
    }];
  }, null);
})();

/* eslint-disable @angular-eslint/directive-selector */
/**
 * Directive to hook into the errors of a given control.
 *
 * Example:
 *
 * ```ts
 * \@Component({
 *   template: `
 *   <div [ngxErrors]="myControl">
 *     <div ngxError="required">This input is required</div>
 *   </div>
 *   `
 * })
 * export class MyComponent {
 *   myControl = new FormControl('', Validators.required)
 * }
 * ```
 * In case the `ngxErrors` directive is a child of a [formGroup], you can specify
 * the control by the control name similarly how you'd do it with formControlName:
 *
 * ```ts
 * \@Component({
 *   template: `
 *   <form [formGroup]="form">
 *     <div ngxErrors="firstName">
 *       <div ngxError="required">This input is required</div>
 *     </div>
 *   </form>
 *   `
 * })
 * export class MyComponent {
 *   form = this.fb.group({
 *     firstName: ['', Validators.required]
 *   });
 *   constructor(private fb: FormBuilder) {}
 * }
 * ```
 */
class ErrorsDirective {
  set _control(val) {
    if (val != null) {
      this._controlInput$.next(val);
    }
  }
  constructor(formDirective, parentFormGroupDirective, parentFormGroupName, config) {
    this.formDirective = formDirective;
    this.parentFormGroupDirective = parentFormGroupDirective;
    this.parentFormGroupName = parentFormGroupName;
    this.config = config;
    this.subs = new Subscription();
    this.control$$ = new BehaviorSubject(undefined);
    this.control$ = this.control$$.asObservable().pipe(filterOutNullish());
    this._controlInput$ = new BehaviorSubject(undefined);
    this.afterViewInit$ = new BehaviorSubject(false);
    this.errorsCouldBeHidden$$ = new BehaviorSubject({});
    this.errorsVisibility$ = this.errorsCouldBeHidden$$.asObservable().pipe(map(errorsCouldBeHidden => {
      const arr = [];
      let visibleCount = 0;
      for (const key in errorsCouldBeHidden) {
        if (Object.prototype.hasOwnProperty.call(errorsCouldBeHidden, key)) {
          const errorCouldBeHidden = errorsCouldBeHidden[key];
          if (!errorCouldBeHidden) {
            visibleCount++;
          }
          const visible = !errorCouldBeHidden && (!this.config.showMaxErrors || visibleCount <= this.config.showMaxErrors);
          arr.push({
            key,
            hidden: !visible
          });
        }
      }
      return arr;
    }), shareReplay(1));
    // initialize directive only after control input was set AND after
    // afterViewInit since parentFormGroupDirective might not be resolved
    // before that
    const $ = combineLatest(this._controlInput$.pipe(filterOutNullish()), this.afterViewInit$.pipe(filter(hasInit => hasInit === true))).pipe(tap(([control]) => {
      this.initDirective(control);
    }));
    this.subs.add($.subscribe());
  }
  ngAfterViewInit() {
    this.afterViewInit$.next(true);
    // Use of the setTimeout to ensure that the @Input _control was surely set
    // in the all cases. In particular the edge-case where ngModelGroup
    // declared via template driven forms results in the control being
    // set later than ngAfterViewInit life-cycle hook is called
    setTimeout(() => {
      this.validateDirective();
    }, 0);
  }
  ngOnDestroy() {
    this.subs.unsubscribe();
  }
  visibilityForKey$(key) {
    return this.errorsVisibility$.pipe(map(errors => errors.find(error => error.key === key)), filterOutNullish(), map(error => error.hidden), distinctUntilChanged());
  }
  visibilityChanged(errorName, showWhen, hidden) {
    const key = `${errorName}-${showWhen}`;
    const val = this.errorsCouldBeHidden$$.getValue();
    if (val[key] !== hidden) {
      const newVal = {
        ...val,
        [key]: hidden
      };
      this.errorsCouldBeHidden$$.next(newVal);
    }
  }
  validateDirective() {
    if (!this._controlInput$.value) {
      throw new NoControlError();
    }
  }
  initDirective(_control) {
    if (typeof _control === 'string') {
      if (!this.parentFormGroupDirective) {
        throw new ParentFormGroupNotFoundError(_control);
      }
      const control = !this.parentFormGroupName ? this.parentFormGroupDirective.form.get(_control) : this.parentFormGroupName.control.get(_control);
      if (control == null) {
        throw new ControlNotFoundError(_control);
      }
      this.control$$.next(control);
      return;
    }
    if (!this.isAbstractControl(_control)) {
      throw new ControlInstanceError();
    }
    this.control$$.next(_control);
  }
  isAbstractControl(control) {
    return control instanceof FormControl || control instanceof FormArray || control instanceof FormGroup;
  }
  static {
    this.ɵfac = function ErrorsDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ErrorsDirective)(i0.ɵɵdirectiveInject(NgxErrorsFormDirective, 12), i0.ɵɵdirectiveInject(i1.FormGroupDirective, 12), i0.ɵɵdirectiveInject(i1.FormGroupName, 12), i0.ɵɵdirectiveInject(ErrorsConfiguration));
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: ErrorsDirective,
      selectors: [["", "ngxErrors", ""]],
      inputs: {
        _control: [0, "ngxErrors", "_control"],
        showWhen: "showWhen"
      },
      exportAs: ["ngxErrors"],
      standalone: false
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ErrorsDirective, [{
    type: Directive,
    args: [{
      selector: '[ngxErrors]',
      exportAs: 'ngxErrors'
    }]
  }], function () {
    return [{
      type: NgxErrorsFormDirective,
      decorators: [{
        type: Optional
      }, {
        type: SkipSelf
      }]
    }, {
      type: i1.FormGroupDirective,
      decorators: [{
        type: Optional
      }, {
        type: SkipSelf
      }]
    }, {
      type: i1.FormGroupName,
      decorators: [{
        type: Optional
      }, {
        type: SkipSelf
      }]
    }, {
      type: ErrorsConfiguration
    }];
  }, {
    _control: [{
      type: Input,
      args: ['ngxErrors']
    }],
    showWhen: [{
      type: Input
    }]
  });
})();
class OverriddenShowWhen {
  constructor() {
    this.controlOverridden$ = new Subject();
    this.controls = new Map();
  }
  get(control) {
    return this.controls.get(control);
  }
  add(control) {
    this.controls.set(control, []);
    this.controlOverridden$.next(control);
  }
  errorVisibilityChanged(control, errorName, showWhen, isVisible) {
    const errorsShown = this.controls.get(control);
    if (!errorsShown) {
      return;
    }
    const key = `${errorName}-${showWhen}`;
    if (isVisible && !errorsShown.includes(key)) {
      const newErrorNamesShown = [...errorsShown, key];
      this.controls.set(control, newErrorNamesShown);
    }
    if (!isVisible && errorsShown.includes(key)) {
      const newErrorNamesShown = errorsShown.filter(name => name !== key);
      this.controls.set(control, newErrorNamesShown);
    }
    this.controlOverridden$.next(control);
  }
  static {
    this.ɵfac = function OverriddenShowWhen_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || OverriddenShowWhen)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: OverriddenShowWhen,
      factory: OverriddenShowWhen.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OverriddenShowWhen, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();

/* eslint-disable @angular-eslint/directive-selector */
/**
 * Directive to provide a validation error for a specific error name.
 * Used as a child of ngxErrors directive.
 *
 * Example:
 * ```html
 * <div [ngxErrors]="control">
 *   <div ngxError="required">This input is required</div>
 * </div>
 * ```
 */
class ErrorDirective {
  constructor(config, errorStateMatchers, overriddenShowWhen, cdr,
  // ErrorsDirective is actually required.
  // use @Optional so that we can throw a custom error
  errorsDirective) {
    this.config = config;
    this.errorStateMatchers = errorStateMatchers;
    this.overriddenShowWhen = overriddenShowWhen;
    this.cdr = cdr;
    this.errorsDirective = errorsDirective;
    this.subs = new Subscription();
    this.hidden = true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.err = {};
    this.err$$ = new BehaviorSubject({});
  }
  ngAfterViewInit() {
    this.validateDirective();
    this.watchForEventsTriggeringVisibilityChange();
  }
  ngOnDestroy() {
    // without the timeout object gets destroyed before call finishes causing errors
    setTimeout(() => {
      this.errorsDirective?.visibilityChanged(this.errorName, this.showWhen, true);
    }, 0);
    this.subs.unsubscribe();
  }
  watchForEventsTriggeringVisibilityChange() {
    const ngSubmit$ = this.errorsDirective.formDirective?.form ? this.errorsDirective.formDirective.form.ngSubmit.asObservable() : NEVER;
    let touchedChanges$;
    const sub = this.errorsDirective.control$.pipe(tap(control => {
      this.initConfig(control);
      this.watchForVisibilityChange(control);
    }), tap(control => {
      touchedChanges$ = extractTouchedChanges(control);
      this.calcShouldDisplay(control);
    }), switchMap(control => {
      // https://github.com/angular/angular/issues/41519
      // control.statusChanges do not emit when there's async validator
      // ugly workaround:
      let asyncBugWorkaround$ = NEVER;
      if (control.asyncValidator && control.status === 'PENDING') {
        asyncBugWorkaround$ = timer(0, 50).pipe(switchMap(() => of(control.status)), filter(x => x !== 'PENDING'), first());
      }
      return merge(control.valueChanges, control.statusChanges, touchedChanges$, ngSubmit$, asyncBugWorkaround$).pipe(auditTime(0), map(() => control));
    }), tap(control => {
      this.calcShouldDisplay(control);
    })).subscribe();
    this.subs.add(sub);
  }
  calcShouldDisplay(control) {
    const hasError = control.hasError(this.errorName);
    const controlError = control.getError(this.errorName) || {};
    if (JSON.stringify(this.err$$.getValue()) !== JSON.stringify(controlError)) {
      this.err$$.next(controlError);
    }
    const form = this.errorsDirective.formDirective?.form ?? null;
    const errorStateMatcher = this.errorStateMatchers.get(this.showWhen);
    if (!errorStateMatcher) {
      throw new InvalidShowWhenError(this.showWhen, this.errorStateMatchers.validKeys());
    }
    const hasErrorState = errorStateMatcher.isErrorState(control, form);
    const couldBeHidden = !(hasErrorState && hasError);
    this.errorsDirective.visibilityChanged(this.errorName, this.showWhen, couldBeHidden);
  }
  watchForVisibilityChange(control) {
    const key = `${this.errorName}-${this.showWhen}`;
    const sub = combineLatest([this.errorsDirective.visibilityForKey$(key), this.err$$]).pipe(tap(([hidden, err]) => {
      this.hidden = hidden;
      this.overriddenShowWhen.errorVisibilityChanged(control, this.errorName, this.showWhen, !this.hidden);
      this.err = err;
      this.cdr.detectChanges();
    })).subscribe();
    this.subs.add(sub);
  }
  initConfig(control) {
    if (this.showWhen) {
      this.overriddenShowWhen.add(control);
      return;
    }
    if (this.errorsDirective.showWhen) {
      this.showWhen = this.errorsDirective.showWhen;
      this.overriddenShowWhen.add(control);
      return;
    }
    this.showWhen = this.config.showErrorsWhenInput;
    if (this.showWhen === 'formIsSubmitted' && !this.errorsDirective.parentFormGroupDirective) {
      this.showWhen = 'touched';
    }
  }
  validateDirective() {
    if (this.errorsDirective == null) {
      throw new NoParentNgxErrorsError();
    }
    if (typeof this.errorName !== 'string' || this.errorName.trim() === '') {
      throw new ValueMustBeStringError();
    }
  }
  static {
    this.ɵfac = function ErrorDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ErrorDirective)(i0.ɵɵdirectiveInject(ErrorsConfiguration), i0.ɵɵdirectiveInject(ErrorStateMatchers), i0.ɵɵdirectiveInject(OverriddenShowWhen), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(ErrorsDirective, 8));
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: ErrorDirective,
      selectors: [["", "ngxError", ""]],
      hostVars: 1,
      hostBindings: function ErrorDirective_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵhostProperty("hidden", ctx.hidden);
        }
      },
      inputs: {
        errorName: [0, "ngxError", "errorName"],
        showWhen: "showWhen"
      },
      exportAs: ["ngxError"],
      standalone: false
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ErrorDirective, [{
    type: Directive,
    args: [{
      // eslint-disable-next-line @angular-eslint/directive-selector
      selector: '[ngxError]',
      exportAs: 'ngxError'
    }]
  }], function () {
    return [{
      type: ErrorsConfiguration
    }, {
      type: ErrorStateMatchers
    }, {
      type: OverriddenShowWhen
    }, {
      type: i0.ChangeDetectorRef
    }, {
      type: ErrorsDirective,
      decorators: [{
        type: Optional
      }]
    }];
  }, {
    hidden: [{
      type: HostBinding,
      args: ['hidden']
    }],
    errorName: [{
      type: Input,
      args: ['ngxError']
    }],
    showWhen: [{
      type: Input
    }]
  });
})();
const declarationsAndExports = [ErrorsDirective, ErrorDirective, NgxErrorsFormDirective];
const defaultConfig = {
  showErrorsWhenInput: 'touched',
  showMaxErrors: undefined
};
function mergeErrorsConfiguration(config) {
  return {
    ...defaultConfig,
    ...config
  };
}
const ERROR_STATE_MATCHER_PROVIDERS = [ErrorStateMatchers, ShowOnTouchedErrorStateMatcher, ShowOnDirtyErrorStateMatcher, ShowOnTouchedAndDirtyErrorStateMatcher, ShowOnSubmittedErrorStateMatcher];
class NgxErrorsModule {
  static configure(config) {
    return {
      ngModule: NgxErrorsModule,
      providers: [...ERROR_STATE_MATCHER_PROVIDERS, {
        provide: ErrorsConfiguration,
        useValue: mergeErrorsConfiguration(config)
      }]
    };
  }
  static {
    this.ɵfac = function NgxErrorsModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NgxErrorsModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: NgxErrorsModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      providers: [ErrorsConfiguration, ...ERROR_STATE_MATCHER_PROVIDERS]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NgxErrorsModule, [{
    type: NgModule,
    args: [{
      declarations: [...declarationsAndExports],
      exports: [...declarationsAndExports],
      providers: [ErrorsConfiguration, ...ERROR_STATE_MATCHER_PROVIDERS]
    }]
  }], null, null);
})();

/**
 * Makes it easy to trigger validation on the control, that depends on
 * a value of a different control
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function dependentValidator(opts) {
  let subscribed = false;
  return formControl => {
    const form = formControl.root;
    const {
      watchControl,
      condition,
      validator
    } = opts;
    const controlToWatch = watchControl(form);
    if (!controlToWatch) {
      if (isDevMode()) {
        console.warn(`dependentValidator could not find specified watchControl`);
      }
      return null;
    }
    if (!subscribed) {
      subscribed = true;
      controlToWatch.valueChanges.subscribe(() => {
        formControl.updateValueAndValidity();
      });
    }
    if (condition === undefined || condition(controlToWatch.value)) {
      const validatorFn = validator(controlToWatch.value);
      return validatorFn(formControl);
    }
    return null;
  };
}

/*
 * Public API Surface of ngx-errors
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CUSTOM_ERROR_STATE_MATCHERS, ControlInstanceError, ControlNotFoundError, ERROR_STATE_MATCHER_PROVIDERS, ErrorDirective, ErrorStateMatchers, ErrorsConfiguration, ErrorsDirective, InvalidShowWhenError, NgxError, NgxErrorsFormDirective, NgxErrorsModule, NoControlError, NoParentNgxErrorsError, OverriddenShowWhen, ParentFormGroupNotFoundError, ShowOnDirtyErrorStateMatcher, ShowOnSubmittedErrorStateMatcher, ShowOnTouchedAndDirtyErrorStateMatcher, ShowOnTouchedErrorStateMatcher, ValueMustBeStringError, dependentValidator, extractTouchedChanges, filterOutNullish, markDescendantsAsDirty };
